import React, { createContext, useContext, useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { getTransactionTypes } from '../api/gettoncini';
import { useAppResources } from './AppResourcesContext';
import { useUserProfile } from './UserProfileContext';

const GettonciniContext = createContext(null);

const isObject = (value) => value != null && typeof value === 'object' && !Array.isArray(value);
const GETTONCINI_EXPIRY_REFRESH_BUFFER_MS = 5000;

export const useGettoncini = () => {
  const context = useContext(GettonciniContext);
  if (!context) {
    return {
      balance: 0,
      isLoading: false,
      transactionTypes: {},
      temporaryLots: [],
      getHintCost: () => null,
      getAIHelperCost: () => null,
      refreshBalance: async () => {},
      optimisticAward: () => {},
      updateBalance: () => {},
      setGettonciniData: () => {},
    };
  }
  return context;
};

export const GettonciniProvider = ({ children }) => {
  const { isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const { appResources, updateAppResourcesLocally } = useAppResources();
  const { isLoading: profileLoading, refreshProfile } = useUserProfile();

  const getCachedBalance = useCallback(() => {
    try {
      const cached = localStorage.getItem('gettoncini-balance');
      if (cached === null) return 0;
      const parsed = parseInt(cached, 10);
      return Number.isFinite(parsed) ? parsed : 0;
    } catch (error) {
      return 0;
    }
  }, []);

  const cacheBalance = useCallback((balance) => {
    try {
      localStorage.setItem('gettoncini-balance', String(balance));
    } catch (error) {
      // Ignore localStorage errors
    }
  }, []);

  const [transactionTypes, setTransactionTypes] = useState({});
  const retryTimeoutRef = useRef(null);
  const expiryRefreshTimeoutRef = useRef(null);
  const [expiryClockMs, setExpiryClockMs] = useState(() => Date.now());

  const gettoncini = isObject(appResources?.gettoncini) ? appResources.gettoncini : null;
  const gettonciniBalance = gettoncini?.balance;
  const temporaryLotsFromResources = Array.isArray(gettoncini?.temporary_lots) ? gettoncini.temporary_lots : [];
  const expiredTemporaryAmount = useMemo(() => {
    return temporaryLotsFromResources.reduce((total, lot) => {
      const remainingAmount = Number(lot?.remaining_amount);
      const expiresAtMs = new Date(lot?.expires_at).getTime();
      if (!Number.isFinite(remainingAmount) || remainingAmount <= 0 || !Number.isFinite(expiresAtMs)) {
        return total;
      }
      return expiresAtMs <= expiryClockMs ? total + remainingAmount : total;
    }, 0);
  }, [expiryClockMs, temporaryLotsFromResources]);
  const activeTemporaryLots = useMemo(() => {
    return temporaryLotsFromResources.filter((lot) => {
      const remainingAmount = Number(lot?.remaining_amount);
      const expiresAtMs = new Date(lot?.expires_at).getTime();
      return Number.isFinite(remainingAmount) && remainingAmount > 0 && Number.isFinite(expiresAtMs) && expiresAtMs > expiryClockMs;
    });
  }, [expiryClockMs, temporaryLotsFromResources]);
  const balance = useMemo(() => {
    if (!isSignedIn) {
      return 0;
    }

    const resourceBalance = gettonciniBalance;
    if (typeof resourceBalance === 'number') {
      return Math.max(0, resourceBalance - expiredTemporaryAmount);
    }
    return getCachedBalance();
  }, [expiredTemporaryAmount, getCachedBalance, gettonciniBalance, isSignedIn]);
  const isLoading = profileLoading;

  useEffect(() => {
    if (typeof balance === 'number') {
      cacheBalance(balance);
    }
  }, [balance, cacheBalance]);

  useEffect(() => {
    const fetchTransactionTypes = async () => {
      try {
        const token = await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null);
        const data = await getTransactionTypes(token);
        const typesMap = {};
        data.transaction_types.forEach((type) => {
          typesMap[type.key] = type;
        });
        setTransactionTypes(typesMap);
      } catch (error) {
        console.error('Failed to fetch transaction types:', error);
      }
    };

    fetchTransactionTypes();
  }, [getToken]);

  useEffect(() => () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (expiryRefreshTimeoutRef.current) {
      clearTimeout(expiryRefreshTimeoutRef.current);
      expiryRefreshTimeoutRef.current = null;
    }
  }, []);

  const setGettonciniData = useCallback((updatesOrUpdater) => {
    updateAppResourcesLocally((currentResources) => {
      const currentGettoncini = isObject(currentResources?.gettoncini)
        ? currentResources.gettoncini
        : {};
      const updates = typeof updatesOrUpdater === 'function'
        ? updatesOrUpdater(currentGettoncini)
        : updatesOrUpdater;
      if (!isObject(updates)) {
        return { gettoncini: currentGettoncini };
      }

      const nextGettoncini = {
        ...currentGettoncini,
        ...updates,
      };

      if (typeof nextGettoncini.balance === 'number') {
        cacheBalance(nextGettoncini.balance);
      }

      return {
        gettoncini: nextGettoncini,
      };
    });
  }, [cacheBalance, updateAppResourcesLocally]);

  const refreshBalance = useCallback(async () => {
    if (!isSignedIn) {
      cacheBalance(0);
      return;
    }

    try {
      const refreshedProfile = await refreshProfile();
      const refreshedBalance = refreshedProfile?.app_resources?.gettoncini?.balance;
      if (typeof refreshedBalance === 'number') {
        cacheBalance(refreshedBalance);
      }
    } catch (error) {
      console.error('Failed to refresh gettoncini balance:', error);
    }
  }, [isSignedIn, cacheBalance, refreshProfile]);

  const triggerExpirySync = useCallback(() => {
    const now = Date.now();
    setExpiryClockMs(now);
    refreshBalance();
  }, [refreshBalance]);

  const lastExpirySyncRef = useRef(0);

  const runExpirySyncSafely = useCallback(() => {
    const now = Date.now();
    if (now - lastExpirySyncRef.current < 1000) {
      return;
    }
    lastExpirySyncRef.current = now;
    triggerExpirySync();
  }, [triggerExpirySync]);

  useEffect(() => {
    if (expiryRefreshTimeoutRef.current) {
      clearTimeout(expiryRefreshTimeoutRef.current);
      expiryRefreshTimeoutRef.current = null;
    }

    if (!isSignedIn || !Array.isArray(temporaryLotsFromResources) || temporaryLotsFromResources.length === 0) {
      return undefined;
    }

    const nextExpiryMs = temporaryLotsFromResources
      .map((lot) => {
        const expiresAtMs = new Date(lot?.expires_at).getTime();
        return Number.isFinite(expiresAtMs) ? expiresAtMs : null;
      })
      .filter((expiresAtMs) => expiresAtMs != null)
      .sort((left, right) => left - right)[0];

    if (nextExpiryMs == null) {
      return undefined;
    }

    const delayMs = Math.max(
      1000,
      nextExpiryMs - Date.now() + GETTONCINI_EXPIRY_REFRESH_BUFFER_MS,
    );

    expiryRefreshTimeoutRef.current = window.setTimeout(() => {
      expiryRefreshTimeoutRef.current = null;
      runExpirySyncSafely();
    }, delayMs);

    return () => {
      if (expiryRefreshTimeoutRef.current) {
        clearTimeout(expiryRefreshTimeoutRef.current);
        expiryRefreshTimeoutRef.current = null;
      }
    };
  }, [isSignedIn, refreshBalance, runExpirySyncSafely, temporaryLotsFromResources]);

  useEffect(() => {
    if (!isSignedIn || !Array.isArray(temporaryLotsFromResources) || temporaryLotsFromResources.length === 0) {
      return undefined;
    }

    const handleVisibilityOrFocus = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        runExpirySyncSafely();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
    };
  }, [isSignedIn, runExpirySyncSafely, temporaryLotsFromResources]);

  const optimisticAward = useCallback((transactionKey, success = true) => {
    if (!isSignedIn) {
      return;
    }

    const txType = transactionTypes[transactionKey];
    if (!txType) {
      console.warn(`Unknown transaction type: ${transactionKey}`);
      return;
    }

    if (success) {
      const optimisticBalance = balance + txType.amount;
      setGettonciniData({ balance: optimisticBalance });
    }
  }, [balance, isSignedIn, setGettonciniData, transactionTypes]);

  const updateBalance = useCallback((newBalance) => {
    if (typeof newBalance === 'number') {
      setGettonciniData({ balance: newBalance });
    }
  }, [setGettonciniData]);

  const getHintCost = useCallback((game, hintType) => {
    const txKey = `hint_${game}_${hintType}`;
    const txType = transactionTypes[txKey];
    if (txType) {
      return Math.abs(txType.amount);
    }
    return null;
  }, [transactionTypes]);

  const getAIHelperCost = useCallback(() => {
    const txType = transactionTypes.ai_helper;
    if (txType) {
      return Math.abs(txType.amount);
    }
    return null;
  }, [transactionTypes]);

  const value = useMemo(() => ({
    balance,
    isLoading,
    transactionTypes,
    temporaryLots: activeTemporaryLots,
    refreshBalance,
    optimisticAward,
    updateBalance,
    setGettonciniData,
    getHintCost,
    getAIHelperCost,
  }), [
    balance,
    isLoading,
    transactionTypes,
    activeTemporaryLots,
    refreshBalance,
    optimisticAward,
    updateBalance,
    setGettonciniData,
    getHintCost,
    getAIHelperCost,
  ]);

  return (
    <GettonciniContext.Provider value={value}>
      {children}
    </GettonciniContext.Provider>
  );
};
